import React, { useCallback, useState } from 'react';
import { getIn } from 'formik';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';

import { FieldElementPropTypes } from '../propTypes';
import { TextField } from 'core/components/text-field';
import { Autocomplete } from 'core/components/autocomplete';
import { FormField } from 'core/components/form';

const CUSTOM_ID = 'isCustom';
const CUSTOM_OPTION_LABEL = 'Add a lender not listed in our database';

const useStyles = makeStyles((theme) => ({
  customOption: {
    fontWeight: 600,
    fontSize: 12,
    lineHeight: '16px',
    color: theme.colorPalette.green_90
  },
  addLenderText: {
    fontWeight: 400,
    color: theme.colorPalette.orange_50
  }
}));

const getFieldIdPrefix = (id) => {
  const lastPeriodIndex = id.lastIndexOf('.');
  // Separate the prefix and the last part
  const prefixFieldId = id.slice(0, lastPeriodIndex + 1);
  return prefixFieldId;
};

const MortgageLenderNameAutocomplete = ({
  field: { id, name, label, mode, options, value, onSelection, helperText, onCustomSelection, ...props },
  ctx: { setFieldValue, touched, errors, classes, setFieldTouched }
}) => {
  const customStyles = useStyles();
  const [inputValue, setInputValue] = useState('');
  const [isCustom, setIsCustom] = useState(() => {
    return value ? !options.some((o) => o.id === value || o.text === value) : false;
  });
  const prefixFieldId = getFieldIdPrefix(id);
  const isCustomFieldId = `${prefixFieldId}${CUSTOM_ID}`;

  const fieldId = isCustom ? isCustomFieldId : id;
  const customOption = { id: isCustomFieldId, text: CUSTOM_OPTION_LABEL, isCustom: true };

  const handleSelectChange = useCallback(
    (_event, val) => {
      setFieldTouched(id, true, false);
      setInputValue(val?.text || '');
      const isCustomOptionSelected = !!val?.isCustom;
      setIsCustom(isCustomOptionSelected);
      if (isCustomOptionSelected) {
        setFieldValue(id, '');
        setFieldValue(`${prefixFieldId}id`, null);
        setFieldValue(`${prefixFieldId}isCustom`, true);
        onCustomSelection?.();
      } else {
        setFieldValue(id, val?.text || '');
        setFieldValue(`${prefixFieldId}isCustom`, false);
        onSelection?.(id, val);
      }
    },
    [setFieldTouched, id, setFieldValue, onSelection, onCustomSelection, prefixFieldId]
  );

  const handleInputChange = useCallback(
    (event) => {
      const {
        target: { value }
      } = event;
      setInputValue(value);
      setFieldTouched(id || name, true, false);
      setFieldValue(id || name, value);
    },
    [id, name, setFieldTouched, setFieldValue]
  );

  const handleBlur = useCallback(
    (event) => {
      const {
        target: { value }
      } = event;
      setFieldTouched(id, true, false);
      setFieldValue(id, value);
    },
    [setFieldTouched, id, setFieldValue]
  );

  let filteredOptions = options.filter((option) => {
    const text = option.text || option.value;
    return (
      text?.toLowerCase()?.includes(inputValue?.toLowerCase()) ||
      option?.id?.toString().toLowerCase().includes(inputValue?.toLowerCase())
    );
  });

  if (inputValue) {
    filteredOptions = filteredOptions.slice(0, 7).concat(customOption);
  }

  const currentValue = inputValue
    ? { id: inputValue, text: inputValue }
    : options.concat(customOption).find((option) => (isCustom ? option.isCustom : option.text === value));

  const inputValueProp = inputValue ? { inputValue } : {};
  return (
    <>
      <Autocomplete
        value={currentValue}
        {...inputValueProp}
        classes={classes}
        name={fieldId}
        id={fieldId}
        options={filteredOptions}
        onSelection={handleSelectChange}
        onBlur={() => {
          setFieldTouched(id, true, false);
          setInputValue('');

          if (!currentValue) {
            setFieldValue(id, '');
          }
        }}
        renderOption={(option) => {
          return option.isCustom ? (
            <div className={customStyles.customOption}>
              Can't find your lender?{' '}
              <span className={customStyles.addLenderText}>Add a lender not listed in our database</span>
            </div>
          ) : (
            option.text
          );
        }}
        errors={errors}
        touched={touched}
        InputComponent={TextField}
        inputProps={{
          label,
          mode,
          onChange: handleInputChange,
          error: getIn(touched, fieldId) && !!getIn(errors, fieldId),
          helperText: getIn(touched, fieldId) ? getIn(errors, fieldId) : helperText || undefined,
          disabled: props.disabled
        }}
        {...props}
      />
      {isCustom && (
        <Grid container justifyContent="space-around" alignItems="flex-start">
          <FormField
            mode={mode}
            id={id}
            name={id}
            label="Lender name"
            type="string"
            xs={12}
            permissions={{ isLicensedAction: false }}
            onBlur={handleBlur}
          />
        </Grid>
      )}
    </>
  );
};

MortgageLenderNameAutocomplete.propTypes = FieldElementPropTypes;

export default MortgageLenderNameAutocomplete;
